import { CaretLeft, WarningCircle } from "@phosphor-icons/react";
import CompletionModal from "./CompletionModal";
import { MIXPANEL_EVENTS } from "constants/mixpanelEvents";
import Mixpanel from "utils/mixpanel";
import { useEffect, useMemo, useState } from "react";
import DocumentsList from "./DocumentsList";
import {
	ANSWER_SOURCE,
	DOC_TYPES,
	getDisplayableDocuments,
	getDisplayableQuestions,
	getValidationResult,
	TEDCApplicant,
	useEDCFlow,
	validateApplicantDocuments,
	validateApplicantQuestions,
	VALIDATION_STATUS
} from "@smvtech/x-flux";
import FormList from "./FormList";
import BottomActionBar from "./FormList/BottomActionBar";
import BottomDrawer from "../components/BottomDrawer";
import ErrorReviewDrawer from "../components/ErrorReviewDrawer";
import ProgressSection from "../components/ProgressSection";
import { Flag } from "x-wings";
import { Flags } from "x-wings/Components/Flag/types";
import { Checkbox } from "antd";
import { useEDCFlowContext } from "pages/EDCFlow/context";

const OCR_SOURCE_TO_DOC_TYPE: Partial<Record<ANSWER_SOURCE, DOC_TYPES>> = {
	[ANSWER_SOURCE.PASSPORT_FRONT_OCR]: DOC_TYPES.PASSPORT_FRONT,
	[ANSWER_SOURCE.PASSPORT_BACK_OCR]: DOC_TYPES.PASSPORT_BACK
};

type TabType = "documents" | "form";

type Props = {
	activeApplicantData: TEDCApplicant;
	onPrev: () => void;
	onNext: () => void;
	initialTab?: "documents" | "form";
	onDocumentsCompleted: () => void;
};

const VisaForm = ({ activeApplicantData, onPrev, onNext, onDocumentsCompleted, initialTab = "documents" }: Props) => {
	const [activeTab, setActiveTab] = useState<TabType>(initialTab ?? "documents");
	const [drawerType, setDrawerType] = useState<"tnc" | "errors" | null>(null);
	const [isTncChecked, setIsTncChecked] = useState(false);
	const [showCompletionModal, setShowCompletionModal] = useState(false);

	const { order, runApplicantValidations } = useEDCFlow();
	const { isSavingAnswer } = useEDCFlowContext();

	useEffect(() => {
		if (initialTab) {
			setActiveTab(initialTab);
		}
	}, [initialTab]);

	const isDocumentsComplete = useMemo(() => {
		const validatedDocs = validateApplicantDocuments(activeApplicantData, [
			ANSWER_SOURCE.USER_INPUT,
			ANSWER_SOURCE.DOCUMENT
		]);

		return validatedDocs.every(
			(doc) =>
				doc.validationResult?.status === VALIDATION_STATUS.PERFECT ||
				doc.validationResult?.status === VALIDATION_STATUS.FIXABLE
		);
	}, [activeApplicantData]);

	const questionBlockingErrors = useMemo(() => {
		return validateApplicantQuestions(activeApplicantData, [ANSWER_SOURCE.PASSPORT_FRONT_OCR]);
	}, [activeApplicantData]);

	const formQuestions = getDisplayableQuestions(activeApplicantData.questions, [ANSWER_SOURCE.PASSPORT_FRONT_OCR]);
	const requiredFormQuestions = formQuestions.filter((q) => q.question.is_required);
	const answeredQuestions = requiredFormQuestions.filter((q) => {
		const validation = getValidationResult(q);
		return validation?.status === VALIDATION_STATUS.PERFECT || validation?.status === VALIDATION_STATUS.FIXABLE;
	});

	const isFormComplete = questionBlockingErrors.length === 0 && answeredQuestions.length === requiredFormQuestions.length;

	const handleContinue = () => {
		Mixpanel.track(MIXPANEL_EVENTS.EDC_FLOW.CONTINUE_CLICKED, {
			tab: activeTab,
			applicantId: activeApplicantData.application_id
		});

		if (activeTab === "documents") {
			if (!isDocumentsComplete) return;
			else onDocumentsCompleted();
			return;
		}

		if (activeTab === "form") {
			if (!isFormComplete) return;

			if (!isDocumentsComplete) {
				setActiveTab("documents");
				return;
			}
			if (isDocumentsComplete && isFormComplete) {
				setDrawerType("tnc");
				return;
			}

			onNext();
		}
	};

	const isContinueDisabled = activeTab === "documents" ? !isDocumentsComplete : !isFormComplete || isSavingAnswer;

	const blockingErrorCount = useMemo(() => {
		const validations = validateApplicantDocuments(activeApplicantData, [
			ANSWER_SOURCE.USER_INPUT,
			ANSWER_SOURCE.DOCUMENT
		]);
		const unfixableCount = validations.filter(
			(v) => v.validationResult?.status === VALIDATION_STATUS.UNFIXABLE
		).length;

		const docTypesWithFormErrors = new Set<DOC_TYPES>();
		activeApplicantData.questions.forEach((q) => {
			const docType = OCR_SOURCE_TO_DOC_TYPE[q.question.answer_source];
			if (!docType) return;
			const validation = getValidationResult(q);
			if (validation?.status && validation.status !== VALIDATION_STATUS.PERFECT) {
				docTypesWithFormErrors.add(docType);
			}
		});

		const documents = getDisplayableDocuments(activeApplicantData.questions, [
			ANSWER_SOURCE.USER_INPUT,
			ANSWER_SOURCE.DOCUMENT
		]);
		const formErrorCount = documents.filter((q) => {
			if (!q.question.doc_type) return false;
			if (!docTypesWithFormErrors.has(q.question.doc_type)) return false;
			const validation = getValidationResult(q);
			return validation?.status === VALIDATION_STATUS.PERFECT || validation?.status === VALIDATION_STATUS.FIXABLE;
		}).length;

		return unfixableCount + formErrorCount;
	}, [activeApplicantData]);
	const unfixableDocuments = useMemo(() => {
		return activeApplicantData.questions.filter((q) => {
			if (!q.question.doc_type) return false;

			const validation = getValidationResult(q);

			return validation?.status === VALIDATION_STATUS.UNFIXABLE;
		});
	}, [activeApplicantData.questions]);

	const documentQuestions = getDisplayableDocuments(activeApplicantData.questions, [
		ANSWER_SOURCE.USER_INPUT,
		ANSWER_SOURCE.DOCUMENT
	]);

	const uploadedDocuments = documentQuestions.filter((q) => {
		const validation = getValidationResult(q);

		return validation?.status === VALIDATION_STATUS.PERFECT || validation?.status === VALIDATION_STATUS.FIXABLE;
	});

	const documentsPercentage =
		documentQuestions.length === 0 ? 0 : Math.round((uploadedDocuments.length / documentQuestions.length) * 100);

	const hasAnyDocumentAttempted = documentQuestions.some((q) => q.answer_data?.assets_id?.length);

	const formPercentage =
		requiredFormQuestions.length === 0
			? 0
			: Math.round((answeredQuestions.length / requiredFormQuestions.length) * 100);

	const pendingDocumentsCount = useMemo(() => {
		return documentQuestions.filter((q) => {
			const validation = getValidationResult(q);

			const notUploaded = !q.answer_data?.assets_id?.length;

			const isUnfixable = validation?.status === VALIDATION_STATUS.UNFIXABLE;

			return notUploaded || isUnfixable;
		}).length;
	}, [documentQuestions]);

	return (
		<>
		<div className="h-dvh bg-white flex flex-col overflow-hidden">
			<div className="bg-white sticky top-0 z-20">
				<div className="flex items-center justify-between px-4 py-[13px] border-b border-gray-100">
					<button onClick={onPrev}>
						<CaretLeft size={20} />
					</button>
					<div className="flex items-center gap-2">
						<Flag code={order?.country.symbol as Flags} size="m" hasBorder hasBorderRadius hasDropShadow />
						<span className="text-sm font-medium text-black">
							{activeApplicantData.traveller.first_name
								? `${activeApplicantData.traveller.first_name}'s${
										activeTab === "documents" ? " Documents" : " Details"
									}`
								: activeTab === "documents"
									? "Documents"
									: "Details"}
						</span>
					</div>
					<div className="w-5" />
				</div>

				<div className="px-4 pt-[34px]">
					<div className="flex bg-gray-100 rounded-xl p-1">
						<button
							onClick={() => setActiveTab("documents")}
							className={`flex-1 text-sm font-medium py-2 rounded-lg transition ${
								activeTab === "documents" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"
							}`}>
							<div className="flex items-center justify-center gap-2">
								<span>Documents</span>

								{pendingDocumentsCount > 0 && activeTab === "form" && (
									<span className="bg-[#F7900A] text-white text-xs font-medium px-2 py-0.5 rounded-full">
										{pendingDocumentsCount}
									</span>
								)}
							</div>
						</button>

						<button
							onClick={() => setActiveTab("form")}
							className={`flex-1 text-sm font-medium py-2 rounded-lg transition ${
								activeTab === "form" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"
							}`}>
							Form
						</button>
					</div>
				</div>

				{activeTab === "form" && pendingDocumentsCount > 0 && (
					<div className="px-4 pt-3 bg-white">
						<div className="w-full bg-[#F7900A] text-white text-sm font-medium rounded-xl py-3 px-4 flex items-center justify-start gap-2 text-left">
							<WarningCircle size={16} weight="fill" className="shrink-0" />
							<span>
								{pendingDocumentsCount} document{pendingDocumentsCount !== 1 && "s"} pending in
								Documents tab.{" "}
								<button
									onClick={() => setActiveTab("documents")}
									className="underline font-semibold">
									View now.
								</button>
							</span>
						</div>
					</div>
				)}

				<ProgressSection
					title={
						activeTab === "documents" ? `${documentsPercentage}% Complete` : `${formPercentage}% Complete`
					}
					subtitle={
						activeTab === "documents"
							? `${uploadedDocuments.length} / ${documentQuestions.length} Documents Uploaded`
							: `${answeredQuestions.length} / ${requiredFormQuestions.length} Questions Answered`
					}
					percentage={activeTab === "documents" ? documentsPercentage : formPercentage}
					showSavedBadge={activeTab === "documents" ? hasAnyDocumentAttempted : formPercentage > 0}
				/>
			</div>
			<div className="flex-1 overflow-y-auto px-4 py-6 bg-white">
				{activeTab === "documents" ? (
					<DocumentsList
						activeApplicantData={activeApplicantData}
						isDocumentsComplete={isDocumentsComplete}
						onDocumentsCompleted={onDocumentsCompleted}
						onViewFormErrors={() => setActiveTab("form")}
					/>
				) : (
					<FormList activeApplicantData={activeApplicantData} />
				)}
				<BottomActionBar
					continueDisabled={isContinueDisabled}
					onContinueClick={handleContinue}
					errorCount={blockingErrorCount}
					onErrorClick={() => {
						if (unfixableDocuments.length > 0) {
							setDrawerType("errors");
						}
					}}
				/>
				<BottomDrawer open={drawerType === "errors"} onClose={() => setDrawerType(null)}>
					<ErrorReviewDrawer documents={unfixableDocuments} activeApplicantData={activeApplicantData} />
				</BottomDrawer>
				<BottomDrawer open={drawerType === "tnc"} onClose={() => setDrawerType(null)}>
					<div className="mt-[14px]">
						<h3 className="text-xl font-semibold pb-[7px]">Terms & Conditions</h3>

						<p className="text-sm text-gray-600 pb-[26px]">
							We highly recommend taking the time to double-check that all the information is correct &
							complete to avoid any delays or issues during the review process.
						</p>

						<Checkbox
							checked={isTncChecked}
							onChange={(e) => {
								setIsTncChecked(e.target.checked);
								if (e.target.checked) {
									Mixpanel.track(MIXPANEL_EVENTS.EDC_FLOW.TNC_ACCEPTED, {
										applicantId: activeApplicantData.application_id
									});
								}
							}}
							className="text-sm pt-[7px]">
							I have reviewed and verified all the documents.
						</Checkbox>

						<button
							disabled={!isTncChecked}
							className={`w-full py-3 rounded-xl text-white transition mt-[20px] bg-gradient-to-r from-indigo-600 to-indigo-800
                            ${!isTncChecked ? "cursor-not-allowed opacity-60" : ""}`}
							onClick={async () => {
								setDrawerType(null);
								setIsTncChecked(false);
								Mixpanel.track(MIXPANEL_EVENTS.EDC_FLOW.FORM_SUBMITTED, {
									applicantId: activeApplicantData.application_id
								});
								try {
									await runApplicantValidations(activeApplicantData.application_id);
									setShowCompletionModal(true);
								} catch {
									return;
								}
							}}>
							Confirm
						</button>
					</div>
				</BottomDrawer>
			</div>
		</div>

		{showCompletionModal && (
			<CompletionModal countryName={order?.country?.name} onClose={onNext} />
		)}
		</>
	);
};

export default VisaForm;

